import { CheckCircle, DollarSign, Download, Plus, RefreshCw, Search, Trash2, Upload, X, XCircle } from "lucide-react";
import React from "react";
import Button from "../../../../components/Button";
import ResponsiveActionButton from "../../../../components/ResponsiveActionButton";
import { useTheme } from "../../../../contexts/ThemeContext";
import { ContractsDownloadProgress } from "../../../../hooks/useContracts";
import { formatFileSize, formatNumber } from "../../../../utils/numberUtils";

interface ContractsActionToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchColumn: string;
  onSearchColumnChange: (value: string) => void;
  optionsAllowed: boolean | undefined;
  onOptionsAllowedChange: (value: boolean | undefined) => void;
  verifiedSinceMs: number | undefined;
  onVerifiedSinceMsChange: (value: number | undefined) => void;
  selectionSize: number;
  loading: boolean;
  downloading: boolean;
  bulkActionLoading: boolean;
  fetchingAllPrices: boolean;
  hasContracts: boolean;
  downloadProgress: ContractsDownloadProgress | null;
  onRefresh: () => void;
  onUpload: () => void;
  onDownload: () => void;
  onCancelDownload: () => void;
  onFetchAllPrices: () => void;
  onAdd: () => void;
  onVerifySelected: () => void;
  onUnverifySelected: () => void;
  onDeleteSelected: () => void;
  onVerifyMatches: () => void;
}

const ContractsActionToolbar: React.FC<ContractsActionToolbarProps> = ({
  searchQuery,
  onSearchChange,
  searchColumn,
  onSearchColumnChange,
  optionsAllowed,
  onOptionsAllowedChange,
  verifiedSinceMs,
  onVerifiedSinceMsChange,
  selectionSize,
  loading,
  downloading,
  bulkActionLoading,
  fetchingAllPrices,
  hasContracts,
  downloadProgress,
  onRefresh,
  onUpload,
  onDownload,
  onCancelDownload,
  onFetchAllPrices,
  onAdd,
  onVerifySelected,
  onUnverifySelected,
  onDeleteSelected,
  onVerifyMatches,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const someSelected = selectionSize > 0;

  const getSearchPlaceholder = () => {
    switch (searchColumn) {
      case "symbol": return "Search by symbol...";
      case "longName": return "Search by name...";
      case "remarks": return "Search by remarks...";
      case "isin": return "Search by ISIN...";
      case "figiComposite": return "Search by FIGI Composite...";
      case "exchange": return "Search by exchange...";
      case "primaryExchange": return "Search by primary exchange...";
      case "currency": return "Search by currency...";
      case "secType": return "Search by type...";
      default: return "Search symbol, name, ISIN...";
    }
  };

  return (
    <div className="flex flex-col gap-4 mb-4">
      {/* Top Actions Row */}
      <div className="flex items-center justify-end gap-2">
        <ResponsiveActionButton
          label="Refresh"
          icon={<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
          onClick={onRefresh}
          disabled={loading}
          variant="default"
        />
        <ResponsiveActionButton
          label="Upload"
          icon={<Upload className="w-4 h-4" />}
          onClick={onUpload}
          variant="default"
        />
        {downloading ? (
          /* The stream carries no Content-Length by design, so there is no total and no
             percentage to show -- a count-up is the honest indicator. */
          <div
            className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
              isDarkMode
                ? "bg-rp-surface border-rp-separator text-rp-grey"
                : "bg-white border-gray-200 text-gray-700"
            }`}
            role="status"
            aria-live="polite"
          >
            <RefreshCw className="w-4 h-4 animate-spin text-rp-amber" />
            <span className="text-sm whitespace-nowrap">
              Downloading... {formatNumber(downloadProgress?.rowCount ?? 0, 0)} contracts
              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                ({formatFileSize(downloadProgress?.byteCount ?? 0)})
              </span>
            </span>
            <button
              type="button"
              onClick={onCancelDownload}
              className="ml-1 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-rp-surface-raised"
            >
              <X className="w-3 h-3" />
              Cancel
            </button>
          </div>
        ) : (
          <ResponsiveActionButton
            label="Download"
            icon={<Download className="w-4 h-4" />}
            onClick={onDownload}
            variant="default"
          />
        )}
        <ResponsiveActionButton
          label={fetchingAllPrices ? "Fetching..." : "Fetch All Prices"}
          icon={<DollarSign className={`w-4 h-4 ${fetchingAllPrices ? "animate-pulse" : ""}`} />}
          onClick={onFetchAllPrices}
          disabled={fetchingAllPrices || !hasContracts}
          variant="default"
        />
        <ResponsiveActionButton
          label="Verify Matches"
          icon={<CheckCircle className="w-4 h-4 text-green-500" />}
          onClick={onVerifyMatches}
          disabled={bulkActionLoading || !hasContracts}
          variant="default"
        />
        <ResponsiveActionButton
          label="Add Contract"
          icon={<Plus className="w-4 h-4" />}
          onClick={onAdd}
          variant="primary"
        />
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Column Search */}
          <select
            value={searchColumn}
            onChange={(e) => onSearchColumnChange(e.target.value)}
            className={`border rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 ${
              isDarkMode
                ? "bg-rp-surface border-rp-separator text-rp-grey focus:ring-rp-amber focus:border-rp-amber"
                : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
            }`}
          >
            <option value="">All Columns</option>
            <option value="symbol">Symbol</option>
            <option value="longName">Name</option>
            <option value="remarks">Remarks</option>
            <option value="isin">ISIN</option>
            <option value="figiComposite">FIGI Composite</option>
            <option value="exchange">Exchange</option>
            <option value="primaryExchange">Primary Exchange</option>
            <option value="currency">Currency</option>
            <option value="secType">Type</option>
          </select>
          
          <div className="relative flex-1 min-w-[200px]">
            <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                }`}
            />
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={getSearchPlaceholder()}
                className={`w-full pl-10 pr-4 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                isDarkMode
                    ? "bg-rp-surface border-rp-separator text-rp-grey placeholder-rp-grey/40 focus:ring-rp-amber focus:border-rp-amber"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500"
                }`}
            />
          </div>

          {/* Options Allowed Filter */}
          <select
            value={optionsAllowed === undefined ? "" : optionsAllowed ? "true" : "false"}
            onChange={(e) => {
              const val = e.target.value;
              onOptionsAllowedChange(val === "" ? undefined : val === "true");
            }}
            className={`border rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 ${
              isDarkMode
                ? "bg-rp-surface border-rp-separator text-rp-grey focus:ring-rp-amber focus:border-rp-amber"
                : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
            }`}
          >
            <option value="">Options: All</option>
            <option value="true">Options: true</option>
            <option value="false">Options: false</option>
          </select>

          {/* Verified Filter */}
          <select
            value={verifiedSinceMs === undefined ? "" : String(verifiedSinceMs)}
            onChange={(e) => {
              const val = e.target.value;
              onVerifiedSinceMsChange(val === "" ? undefined : Number(val));
            }}
            className={`border rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 ${
              isDarkMode
                ? "bg-rp-surface border-rp-separator text-rp-grey focus:ring-rp-amber focus:border-rp-amber"
                : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
            }`}
          >
            <option value="">Verified: All</option>
            <option value="86400000">Verified: Last 24h</option>
            <option value="604800000">Verified: Last 7d</option>
            <option value="2592000000">Verified: Last 30d</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          {/* Bulk Actions */}
          {someSelected && (
            <div
              className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                isDarkMode ? "bg-rp-surface border border-rp-separator" : "bg-white border border-gray-200"
              }`}
            >
              <span className={`text-sm font-medium ${isDarkMode ? "text-rp-amber" : "text-blue-600"}`}>
                {selectionSize} selected
              </span>
              <div className="h-4 w-px bg-gray-500/30 mx-1" />
              <Button
                onClick={onVerifySelected}
                disabled={bulkActionLoading}
                variant="ghost"
                className={`gap-2 ${
                  isDarkMode
                    ? "bg-green-900/30 text-green-400 hover:bg-green-900/50 border border-green-900/50"
                    : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                }`}
                size="sm"
              >
                <CheckCircle className="w-4 h-4" />
                Verify
              </Button>
              <Button
                onClick={onUnverifySelected}
                disabled={bulkActionLoading}
                variant="ghost"
                className={`gap-2 ${
                  isDarkMode
                    ? "bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50 border border-yellow-900/50"
                    : "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200"
                }`}
                size="sm"
              >
                <XCircle className="w-4 h-4" />
                Unverify
              </Button>
              <Button
                onClick={onDeleteSelected}
                disabled={bulkActionLoading}
                variant="ghost"
                className={`gap-2 ${
                  isDarkMode
                    ? "bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-900/50"
                    : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                }`}
                size="sm"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractsActionToolbar;
